import { Component, OnInit, OnDestroy, Inject, LOCALE_ID } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl, UntypedFormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Customer, ManagedUserFilterLists, OrderType } from '@pos-app/data';
import { ExternalUser, ManagedExternalUser, ReportToUser } from '../../+state/user.model';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { ManageusersPartialState } from '../../+state/manageusers.reducer';
import { Store, select } from '@ngrx/store';
import { manageusersQuery } from '../../+state/manageusers.selectors';
import {
  LoadRegisteredUsers,
  EditExternalUserSecurityFunctions,
  LoadSecurityFunctions,
  ResetRegisteredList,
  LoadLookupLists,
  UpdateAccountCustomerSettings,
  SavedSelectedCustomer,
  SavedTempSelectedCustomer,
} from '../../+state/manageusers.actions';
import { formatDate, NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import {
  ApiService,
  CoreUiPartialState,
  isExternalUser,
  isProgrammeUser,
  DialogService,
  hasUnsavedChanges,
  MessageService,
  getDefaultCustomer,
  getOrderTypes,
} from '@pos-app/core-ui';
import { takeUntil, switchMap, take, filter, map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgbDateStruct, NgbDateParserFormatter, NgbCalendar, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { OrdersPartialState } from '../../../orders/+state/orders.reducer';
import { LoadParkedOrdersList } from '../../../orders/+state/orders.actions';
import moment from 'moment';
import { ActivatedRoute, Router } from '@angular/router';
import { ORDER_TYPES } from '@pos-app/data';
import { OOESessionService } from '@pos-app/core-ui/lib/services/ooe-session.service';
import { IOOEUserState } from '../../../epc/models/userState.model';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { MatIcon } from '@angular/material/icon';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';

@Component({
    selector: 'app-manageusers-external',
    templateUrl: './manageusers-external.component.html',
    styleUrls: ['./manageusers-external.component.scss'],
    imports: [CoreUiModule, NgIf, FormsModule, ReactiveFormsModule, NgFor, MatIcon, NgClass, NgbInputDatepicker, NgSelectComponent, NgOptionComponent, AsyncPipe]
})
export class ManageusersExternalComponent implements OnInit, OnDestroy {
  registerUserForm: UntypedFormGroup;
  selectedCustomer: Customer;
  registeredUsers$: Observable<ManagedExternalUser[]>;
  registeredUserSearch = new UntypedFormControl('');
  disabledUserSearch = new UntypedFormControl(false);
  securityFunctionList$: Observable<string[]>;
  userForms: UntypedFormGroup[] = [];
  preSelectedCustomerNumber = false;
  newRegisteredEmail: string;
  minDate: NgbDateStruct;
  globalUserSecurityForm: UntypedFormGroup;
  lookupLists$: Observable<ManagedUserFilterLists>;
  isBusy$: Observable<boolean>;
  externalUserList: ExternalUser[];
  selectedCustomerName: string;
  isCustomerRegistered$: Observable<boolean>;
  selectedCustomer$: Observable<Customer>;
  ARBuCohortsList: Array<string> = [];
  reportToUserList: Array<ReportToUser> = [];
  isARBuAvailable: boolean = false;
  private unSubscribe$ = new Subject<void>();
  filteredSecurityFunctionList: string[] = [];
  filteredOrderTypeList: any;
  orderTypes: OrderType[];
  initialValueArray: any[] = [];

  public get ooeUserState(): IOOEUserState {
    return this.ooeSessionService.getOOEUserState();
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store: Store<ManageusersPartialState>,
    private orderStore: Store<OrdersPartialState>,
    private apiService: ApiService,
    private toastr: ToastrService,
    private ngbCalendar: NgbCalendar,
    private formatter: NgbDateParserFormatter,
    private coreUIStore: Store<CoreUiPartialState>,
    private dialogService: DialogService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private ooeSessionService: OOESessionService,
    private router: Router,
    @Inject(LOCALE_ID) public locale
  ) {
    this.registerUserForm = this.formBuilder.group({
      email: ['', [Validators.email, Validators.required]],
      name: ['', Validators.required],
      //get selected customer value from route
    });

    this.globalUserSecurityForm = this.formBuilder.group({
      branch: [''],
      inventoryBranches: [''],
      inventoryView: ['', Validators.required],
      customerNumber: [''],
      currencyCode: [''],
      displayCurrencyYN: [false],
      displayIncTaxYN: [false],
      displayExTaxYN: [false],
      showNationalInventoryYN: [false],
      ARBuAvailableYN: [false],
    });
  }

  ngOnInit() {
    this.store.dispatch(new ResetRegisteredList());
    this.store.dispatch(new LoadLookupLists());
    this.dispatchParkedOrdersList();
    this.lookupLists$ = this.store.select(manageusersQuery.getLookupLists);
    this.isBusy$ = this.store.select(manageusersQuery.getIsBusy);
    moment.locale(this.locale);

    this.fetchARBuCohorts();

    combineLatest([this.coreUIStore.pipe(select(isExternalUser)), this.coreUIStore.pipe(select(isProgrammeUser))])
      .pipe(
        take(1),
        switchMap(([externalUser, programmeUser]) => {
          if (!(externalUser || programmeUser)) {
            return of(null);
          }

          this.preSelectedCustomerNumber = true;
          Object.values(this.globalUserSecurityForm.controls).forEach((control) => control.disable());

          const fetchAndSelectCustomer = (customerNumber: string) => {
            if (!customerNumber) return of(null);
            return this.apiService.fetchCustomerDetails({ customerNumber }).pipe(
              takeUntil(this.unSubscribe$),
              map((data) => {
                if (data?.ErrorFlag === '0' && data.SearchResults?.[0]) {
                  this.selectCustomer(data.SearchResults[0]);
                }
                return null;
              })
            );
          };

          if (this.ooeUserState) {
            return fetchAndSelectCustomer(this.ooeUserState.externalUsersUsualCustomerNumber);
          } else {
            return this.ooeSessionService.ooeUserState$.pipe(
              takeUntil(this.unSubscribe$),
              take(1),
              switchMap(() => fetchAndSelectCustomer(this.ooeUserState?.externalUsersUsualCustomerNumber))
            );
          }
        })
      )
      .subscribe();

    this.registeredUsers$ = this.store.pipe(select(manageusersQuery.getExternalRegisteredUsers));
    this.securityFunctionList$ = this.store.pipe(select(manageusersQuery.getSecurityFunctionList));

    this.isCustomerRegistered$ = this.store.pipe(select(manageusersQuery.getIsCustomerRegistered));
    this.selectedCustomer$ = this.store.pipe(select(manageusersQuery.getSelectedCustomer));
    this.selectedCustomer$.pipe(takeUntil(this.unSubscribe$)).subscribe((selectedCustomer) => {
      if (selectedCustomer) {
        this.selectCustomer(selectedCustomer);
      }
    });
    this.registeredUsers$.pipe(takeUntil(this.unSubscribe$)).subscribe((users) => {
      if (this.disabledUserSearch.value) {
        users = users.filter((el) => el.enableAccess === false);
      }
      if (users) {
        this.userForms = [];
        this.reportToUserList = [{ name: '', email: '' }];
        let addedUser = null;
        users.map((user) => {
          this.reportToUserList.push({ name: user.name, email: user.email });
          const userForm = this.formBuilder.group(
            {
              email: [user.email],
              name: [user.name],
              dateCreated: [this.displayDate(user.dateCreated)],
              createdBy: [user.createdBy],
              dateLastLogin: [this.displayDate(user.dateLastLogin)],
              dateEffective: [this.displayDate(user.dateEffective)],
              dateExpiry: [this.displayDate(user.dateExpiry)],
              enableAccess: [user.enableAccess],
              securityFunctions: [user.securityFunctions],
              validOrderTypes: [user.validOrderTypes],
              ARBuCohort: [user.ARBuCohort],
              ARBuReportsTo: [user.ARBuReportsTo],
              shipTo: [user?.shipTo?.toString() ?? this.selectedCustomer?.shipTos[0]?.shipToNumber],
            },
            { emitEvent: false }
          );

          if (this.newRegisteredEmail && this.newRegisteredEmail === user.email) {
            addedUser = userForm;
            this.registerUserForm.patchValue({ email: '', name: '' }, { emitEvent: false });
          } else {
            this.userForms.push(userForm);
          }
        });

        this.initialValueArray = this.userForms.map((form) => form.value);

        if (addedUser) {
          this.userForms.unshift(addedUser);
        }
      }
    });
    this.store.dispatch(new LoadSecurityFunctions({ type: 'external' }));
    const today = new Date();
    this.minDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDay(),
    };

    this.apiService
      .getExternalUserList({ activeOnlyYN: 'Y', customerOnlyYN: 'Y' })
      .pipe()
      .subscribe((res) => {
        this.externalUserList = res.SearchResults;
      });

    this.disabledUserSearch.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((val) => this.filterDisabledUser(val));
    this.coreUIStore
      .pipe(select(getOrderTypes))
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        this.orderTypes = data;
      });

    this.store
      .select(manageusersQuery.getTempSelectedCustomer)
      .pipe(take(1)) // Only take the first value and complete
      .subscribe((customer) => {
        if (customer) {
          // Map CustomerDetails -> Customer filling missing fields with sensible defaults
          this.selectedCustomer = customer;
          this.selectCustomer(this.selectedCustomer);
          // Clear immediately after consuming it
          this.store.dispatch(new SavedTempSelectedCustomer(null));
        } else {
          // If no temp customer, clear any previously selected customer to start fresh
          this.store.dispatch(new SavedSelectedCustomer(null));
          this.selectedCustomer = null;
        }
      });
    this.coreUIStore.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
    this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
  }
  checkFormDirty() {
    //TODO: refactor this to use formGroup dirty state
    setTimeout(() => {
      this.userForms.forEach((form, i) => {
        if (form.dirty) {
          this.compareValueWithInitial(i);
        } else {
          form.markAsPristine();
        }
      });
    }, 100);
  }
  get shipTos(): UntypedFormArray {
    return this.globalUserSecurityForm.get('shipTos') as UntypedFormArray;
  }

  ngOnDestroy() {
    this.store.dispatch(new SavedSelectedCustomer(null));
    // Don't clear SavedTempSelectedCustomer here - it needs to survive navigation
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  selectCustomer(e) {
    this.selectedCustomer = e;

    if (this.selectedCustomer && this.selectedCustomer.customerNumber) {
      this.filteredSecurityFunctionList = [];
      e?.securityFunctionsAllowed?.filter((x) => {
        if (x?.securityFunction && x?.allowedTF) {
          return this.filteredSecurityFunctionList.push(x?.securityFunction);
        }
      });
      this.filteredOrderTypeList = [];
      ORDER_TYPES?.filter((y) => {
        if (e.validOrderTypes?.some((el) => el === y?.OrderTypeCode)) {
          return this.filteredOrderTypeList.push(y);
        }
      });
      this.hasARBuAvailable(this.selectedCustomer.ARBuAvailableYN === 'Y');
      this.globalUserSecurityForm.patchValue({
        branch: this.selectedCustomer.branchCode,
        inventoryBranches: this.selectedCustomer.inventoryBranches?.map((x) => x.BranchCode),
        customerNumber: this.selectedCustomer.customerNumber,
        inventoryView: this.selectedCustomer.inventoryView,
        currencyCode: this.selectedCustomer.currencyCode,
        displayCurrencyYN: this.selectedCustomer.displayCurrencyYN === 'Y',
        displayExTaxYN: this.selectedCustomer.displayExTaxYN === 'Y',
        displayIncTaxYN: this.selectedCustomer.displayIncTaxYN === 'Y',
        showNationalInventoryYN: this.selectedCustomer.showNationalInventoryYN === 'Y',
        ARBuAvailableYN: this.selectedCustomer.ARBuAvailableYN === 'Y',
      });

      this.store.dispatch(
        new LoadRegisteredUsers({
          CustomerABNumber: this.selectedCustomer.customerNumber,
          // includeDisabledUsers: true
        })
      );
    } else {
      this.store.dispatch(new ResetRegisteredList());
    }
  }

  selectExternalCustomer(e) {
    this.reset();
    if (e?.parentCustomerNumber) {
      let customer;
      this.apiService
        .fetchCustomerDetails({
          customerNumber: e?.parentCustomerNumber,
        })
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((data) => {
          if (data?.ErrorFlag === '0') {
            customer = data.SearchResults[0];
            this.selectedCustomer = data.SearchResults[0];
            this.hasARBuAvailable(customer.ARBuAvailableYN === 'Y');
            this.globalUserSecurityForm.patchValue({
              customer: customer.customerNumber + ' - ' + customer.customerName,
              branch: customer.branchCode,
              partnerProgrammeCodes: customer.partnerProgrammeCodes.map((x) => x),
              allowedBranches: customer.allowedBranches.map((x) => x.branchCode),
              allowedNonCorps: customer.allowedNonCorps.map((x) => x.partnerCode),
              customerNumber: customer.customerNumber,
              inventoryView: customer.inventoryView,
              validOrderTypes: customer.validOrderTypes.map((x) => x),
              currencyCode: customer.currencyCode,
              displayCurrencyYN: customer.displayCurrencyYN === 'Y',
              displayExTaxYN: customer.displayExTaxYN === 'Y',
              displayIncTaxYN: customer.displayIncTaxYN === 'Y',
              showNationalInventoryYN: customer.showNationalInventoryYN === 'Y',
              ARBuAvailableYN: customer.ARBuAvailableYN === 'Y',
              saveQuotesTF: customer.saveQuotesTF,
              defaultXRefPartsTF: customer.defaultXRefPartsTF,
              shipTos: customer.shipTos?.map((x) => this.shipTos?.push(this.createShipToGroup(x))),
            });
          }
        });
      this.store.dispatch(
        new LoadRegisteredUsers({
          CustomerABNumber: e?.parentCustomerNumber,
          // includeDisabledUsers: true
        })
      );
    } else {
      this.store.dispatch(new ResetRegisteredList());
    }
  }

  createShipToGroup(shipTo): UntypedFormGroup {
    return this.formBuilder.group({
      shipToNumber: [shipTo.shipToNumber],
      shipToName: [shipTo.shipToName],
      shipTo: [shipTo.shipTo],
      corpBranchTF: [shipTo.corpBranchTF],
    });
  }

  isFocus(e) {
    this.reset();
    e.target.select();
  }

  reset() {
    this.globalUserSecurityForm.reset();
    this.selectedCustomer = null;
  }

  hideRow(i) {
    if (this.registeredUserSearch.value === '' && !this.disabledUserSearch.value) {
      return false;
    } else {
      return (
        this.userForms[i].controls.name.value.toLowerCase().indexOf(this.registeredUserSearch.value.toLowerCase()) === -1 &&
        this.userForms[i].controls.email.value.toLowerCase().indexOf(this.registeredUserSearch.value.toLowerCase()) === -1
      );
    }
  }

  toggleFunction(i, event) {
    const functionName = event.target.name;
    let userFunctions = this.userForms[i].controls.securityFunctions.value as string[];
    userFunctions = Object.assign([], userFunctions);
    if (event.target.checked) {
      userFunctions.push(functionName);
    } else {
      userFunctions = userFunctions.filter((x) => x !== functionName);
    }
    this.userForms[i].patchValue({ securityFunctions: userFunctions }, { emitEvent: false });
    //compare value from initialValueArray to mark as dirty
    this.compareValueWithInitial(i);
  }
  compareValueWithInitial(i) {
    if (this.initialValueArray[i] && JSON.stringify(this.userForms[i].value) !== JSON.stringify(this.initialValueArray[i])) {
      this.userForms[i].markAsDirty();
      this.coreUIStore.dispatch(hasUnsavedChanges({ unsavedChanges: true }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: true }));
    } else {
      this.userForms[i].markAsPristine();
      this.coreUIStore.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
    }
  }
  toggleOrderType(i, j, event) {
    //get checked order type fro
    let userOrderTypes = this.userForms[i]?.controls.validOrderTypes.value || [];
    if (event.target.checked) {
      if (this.filteredOrderTypeList[j]?.OrderTypeCode) {
        userOrderTypes = [...userOrderTypes, this.filteredOrderTypeList[j].OrderTypeCode];
      }
    } else {
      userOrderTypes = userOrderTypes.filter((x) => x !== this.filteredOrderTypeList[j].OrderTypeCode);
    }
    this.userForms[i].patchValue({ validOrderTypes: userOrderTypes }, { emitEvent: false });
    //this.userForms[i].markAsDirty();
    this.compareValueWithInitial(i);
  }
  save(i) {
    //if maintainorders is selected then validOrderTypes should be checked
    if (this.showOrderType(i, this.filteredSecurityFunctionList)) {
      if (this.userForms[i].controls.validOrderTypes.value.length === 0) {
        this.toastr.error('Please select at least one order type for Maintain Orders Security or uncheck it');
        return;
      }
    }

    if (this.userForms[i].dirty) {
      const dateEffective = this.formatter.parse(this.userForms[i].controls.dateEffective.value);
      const dateExpiry = this.formatter.parse(this.userForms[i].controls.dateExpiry.value);

      if (!this.checkValidDate(dateEffective, dateExpiry)) {
        return;
      }

      this.store.dispatch(
        new EditExternalUserSecurityFunctions({
          email: this.userForms[i].controls.email.value,
          name: this.userForms[i].controls.name.value,
          actionCode: 'C',
          parentCustomer: this.selectedCustomer.customerNumber,
          effectiveDate: formatDate(new Date(dateEffective.year, dateEffective.month - 1, dateEffective.day), 'yyyy-MM-ddThh:mm:ss', 'en-au'),
          expiryDate: formatDate(new Date(dateExpiry.year, dateExpiry.month - 1, dateExpiry.day), 'yyyy-MM-ddThh:mm:ss', 'en-au'),
          active: this.userForms[i].controls.enableAccess.value,
          securityFunctions: this.userForms[i].controls.securityFunctions.value,
          ARBuCohort: this.userForms[i].controls.ARBuCohort.value,
          ARBuReportsTo: this.userForms[i].controls.ARBuReportsTo.value,
          shipTo: this.userForms[i].controls.shipTo.value,
          validOrderTypes: this.userForms[i].controls.validOrderTypes.value,
        })
      );
      this.coreUIStore.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
    }
  }

  registerUser() {
    this.store.dispatch(
      new EditExternalUserSecurityFunctions({
        ...this.registerUserForm.value,
        actionCode: 'A',
        parentCustomer: this.selectedCustomer.customerNumber,
        effectiveDate: formatDate(Date.now(), 'yyyy-MM-ddThh:mm:ss', 'en-au'),
        expiryDate: '2040-01-01T00:00:00',
        active: true,
        securityFunctions: [],
        validOrderTypes: [],
      })
    );
    this.newRegisteredEmail = this.registerUserForm.controls.email.value;
    //this.registerUserForm.patchValue({ email: '', name: '' });
  }

  hasFunction(i, f) {
    return this.userForms[i].controls.securityFunctions.value.indexOf(f) !== -1;
  }

  hasOrderType(i, f) {
    return this.userForms[i].controls.validOrderTypes.value.indexOf(f) !== -1;
  }

  resetPassword(i) {
    this.dialogService
      .confirm(
        `The password will be reset and a new temporary password will be emailed.
        Are you sure you want to reset the password for this user?`
      )
      .pipe(
        take(1),
        switchMap((sel) => {
          if (sel) {
            return this.apiService.forgottenPassword({
              parentCustomer: this.selectedCustomer?.customerNumber,
              email: this.userForms[i].controls.email.value,
            });
          }
          return of(null);
        })
      )
      .pipe(
        filter((v) => v != null),
        takeUntil(this.unSubscribe$)
      )
      .subscribe(
        () => {
          this.toastr.success('Password reset email sent');
        },
        () => {
          this.toastr.error('Failed to send new password email. Please try again');
        }
      );
  }

  displayDate(dateStr) {
    // formatDate(dateStr, 'dd/MM/yyyy', 'en-au');
    return moment(dateStr).format('L');
  }

  checkValidDate(dateEffective: NgbDateStruct, dateExpiry: NgbDateStruct) {
    const validExpiry = new Date(dateExpiry.year, dateExpiry.month - 1, dateExpiry.day) > new Date();
    if (!validExpiry) {
      this.toastr.error('Invalid expiry date');
      return false;
    }
    const validSetDate =
      new Date(dateExpiry.year, dateExpiry.month - 1, dateExpiry.day) > new Date(dateEffective.year, dateEffective.month - 1, dateEffective.day);

    if (!validSetDate) {
      this.toastr.error('Effective date must be before expiry date');
      return false;
    }
    return true;
  }

  updateGlobalSetting() {
    if (!this.globalUserSecurityForm.controls.branch.value) {
      this.globalUserSecurityForm.patchValue({ branch: '' });
    }
    if (!this.globalUserSecurityForm.controls.validOrderTypes.value) {
      this.globalUserSecurityForm.patchValue({ validOrderTypes: [] });
    }
    this.store.dispatch(
      new UpdateAccountCustomerSettings({
        ...this.globalUserSecurityForm.value,
        displayCurrencyYN: this.globalUserSecurityForm.controls.displayCurrencyYN.value ? 'Y' : 'N',
        displayExTaxYN: this.globalUserSecurityForm.controls.displayExTaxYN.value ? 'Y' : 'N',
        displayIncTaxYN: this.globalUserSecurityForm.controls.displayIncTaxYN.value ? 'Y' : 'N',
        showNationalInventoryYN: this.globalUserSecurityForm.controls.showNationalInventoryYN.value ? 'Y' : 'N',
        ARBuAvailableYN: this.globalUserSecurityForm.controls.ARBuAvailableYN.value ? 'Y' : 'N',
      })
    );

    this.store
      .select(manageusersQuery.getARBuAvailable)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((response) => {
        this.hasARBuAvailable(response);
      });
  }

  onSelectUser(user: ExternalUser) {
    this.selectedCustomer = null;
    this.selectedCustomerName = null;
    this.userForms = [];

    if (user) {
      this.apiService
        .customerSearch({
          customerfuzzySearch: '',
          customerNumber: +user.parentCustomerNumber,
          customerType: '',
        })
        .pipe(map((result) => result?.SearchResults?.find((item) => +item.customerNumber === +user.parentCustomerNumber)))
        .subscribe((customer) => {
          if (customer) {
            this.selectCustomer(customer);
          }
        });
    }
  }

  dispatchParkedOrdersList() {
    this.orderStore.dispatch(new LoadParkedOrdersList());
  }

  fetchARBuCohorts() {
    this.ARBuCohortsList = [''];
    this.apiService
      .fetchARBuCohorts({})
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        if (res.SearchResults && res.SearchResults.length > 0) {
          this.ARBuCohortsList = res.SearchResults.map((result) => {
            return result.Description;
          });
          this.ARBuCohortsList.unshift('');
        }
      });
  }

  hasARBuAvailable(ARBuAvailableYN: boolean) {
    this.isARBuAvailable = ARBuAvailableYN;
  }

  filterDisabledUser(checked) {
    checked
      ? this.store.dispatch(
          new LoadRegisteredUsers({
            CustomerABNumber: this.selectedCustomer.customerNumber,
            includeDisabledUsers: true,
          })
        )
      : this.store.dispatch(
          new LoadRegisteredUsers({
            CustomerABNumber: this.selectedCustomer.customerNumber,
          })
        );
  }

  showOrderType(i, filteredSecurityFunctionList) {
    //if security function contains MaintainOrders checkted then show order types
    if (filteredSecurityFunctionList && filteredSecurityFunctionList.length > 0) {
      const hideOrderType = this.userForms[i].controls.securityFunctions.value.some((x) => x === 'MaintainOrders');
      if (!hideOrderType) {
        this.userForms[i].patchValue({ validOrderTypes: [] });
      }
      return hideOrderType;
    } else {
      return false;
    }
  }

  resetOrderType(i) {
    this.userForms[i].patchValue({ orderTypes: [] });
    this.userForms[i].markAsDirty();
  }

  getOrderTypeDescription(orderType: string) {
    const orderTypeObj = this.orderTypes.find((x) => x.OrderTypeCode === orderType);
    if (orderTypeObj) {
      return orderTypeObj.OrderTypeDescription;
    }
    return '';
  }

  goToManagePartner() {
    this.store.dispatch(new SavedTempSelectedCustomer(this.selectedCustomer));
    this.router.navigate(['/pos/partner/manage']);
  }
}

